import { useDiagnosticsComponentContext } from '../contexts/DiagnosticsComponentContext';
import Loader from '../components/Loader';
import { useEffect, useState } from 'react';
import { useDiagnosticComponentCallbacks } from '../hooks/useDiagnosticsComponentCallbacks';
import { Box, Fab, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  CasesLayout,
  CaseContent,
  CaseData,
  CasesProviders,
  NavigationProvider,
  gekkoApi,
} from '@gekkovet-oy/shared-components';
import { ChatView } from './diagnostics/chat/ChatView';
import { awsConfig } from '../config/aws.config';

export const DiagnosticsComponent = ({
  searchParams,
  sessionId,
  enableChat = false,
}: {
  sessionId: string;
  searchParams: URLSearchParams;
  enableChat?: boolean;
}) => {
  useDiagnosticComponentCallbacks();
  const { session, setSignature, setTimestamp, setSessionId } =
    useDiagnosticsComponentContext();
  useEffect(() => {
    const signature = searchParams.get('signature');
    const timestamp = searchParams.get('timestamp');
    setSignature(signature || 'dummy');
    setTimestamp(timestamp || 'dummy');
  }, [searchParams, setSignature, setTimestamp]);

  const [currentCase, setCase] = useState<CaseData | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    setSessionId(sessionId);
  }, [sessionId, setSessionId]);

  useEffect(() => {
    if (!session) return;
    const { case_id } = session.data;

    if (case_id) {
      gekkoApi.get<CaseData>(`/cases/${case_id}`).then((response) => {
        setCase(response);
      });
    } else {
      const { notes, patient, attachment_ids } = session.data;
      const patientInfo = patient
        ? `\nPatient: ${patient.species}${
            patient.breed ? `, ${patient.breed}` : ''
          }, ${patient.weight_kg}kg, ${patient.gender}`
        : '';
      const body: Record<string, unknown> = {
        notes: notes + patientInfo,
      };
      if (attachment_ids && attachment_ids.length > 0) {
        body.attachments = attachment_ids.map((id) => ({ id }));
      }
      gekkoApi.post<CaseData>('/cases', { body }).then((response) => {
        setCase(response);
      });
    }
  }, [session]);

  if (!session) {
    return <Loader showLoader={true} />;
  }
  if (!currentCase) {
    return <Loader showLoader={true} />;
  }
  if (enableChat && currentCase.id) {
    return (
      <Box sx={{ height: '100vh', overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <ChatView
            caseId={currentCase.id}
            endpoint={awsConfig.COPILOT_WS_URL}
            onOpenDiagnostics={() => setShowDiagnostics(true)}
          />
        </Box>
        {showDiagnostics && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              bgcolor: 'background.default',
            }}
          >
            <CasesProviders case={currentCase}>
              <NavigationProvider>
                <CasesLayout isStandalone>
                  <CaseContent />
                </CasesLayout>
              </NavigationProvider>
            </CasesProviders>
            <Tooltip title="Back to chat">
              <Fab
                size="medium"
                color="primary"
                onClick={() => setShowDiagnostics(false)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 2,
                }}
                aria-label="Back to chat"
              >
                <ArrowBackIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <CasesProviders case={currentCase}>
      <NavigationProvider>
        <Box sx={{ height: '100vh', overflow: 'hidden', position: 'relative' }}>
          <CasesLayout isStandalone>
            <CaseContent />
          </CasesLayout>
        </Box>
      </NavigationProvider>
    </CasesProviders>
  );
};
